import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * ALPINULIN Angebots-Leiter — ein Kaufweg für drei Angebotsstufen.
 *
 * Jede Radio-Option bringt ihre eigene Warenkorb-Payload als `data-lines` mit:
 *   [{ "id": <variantId>, "quantity": 1, "plan": <sellingPlanId|null> }, …]
 * Das Kern-Abo liefert qty×Produkt (+ weitere bezahlte Komponenten + optional
 * die Zugaben der 1. Lieferung ohne Plan), Anker und Einmalkauf je eine Zeile,
 * das Voll-Protokoll n Zeilen. Plan-IDs sind pro Produkt eigene (Engine-Gruppe).
 *
 * Warum ein eigener Writer statt product-form.js: der Horizon-Standardpfad
 * postet genau EIN Line-Item mit maximal EINEM selling_plan. Ein Protokoll
 * braucht mehrere Zeilen mit je eigenem Plan.
 *
 * Varianten-Korrektheit: Die Payload wird serverseitig einmal gerendert und der
 * Block ist gegen den Section-Morph geschützt (Auswahl soll erhalten bleiben).
 * Damit Anzeige und Kauf bei einem Variantenwechsel nicht auseinanderlaufen,
 * lesen die PRODUKT-Stufen ihre Varianten-ID zum Klickzeitpunkt aus `?variant=`
 * — Horizon hält den Parameter beim Variantenwechsel aktuell.
 * (Die Protokoll-Stufe ist davon unberührt: sie kauft fremde Produkte.)
 */
class OfferLadder extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-ladder-button]');
    this.errorEl = this.querySelector('[data-ladder-error]');
    this.summaryEl = this.querySelector('[data-ladder-summary]');
    this.totalEl = this.querySelector('[data-ladder-total]');

    this.button?.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleGiftLink);
    this.addEventListener('click', this.#handleOnceStep);
    this.addEventListener('input', this.#handleOnceInput);
    this.#syncSummary();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleGiftLink);
    this.removeEventListener('click', this.#handleOnceStep);
    this.removeEventListener('input', this.#handleOnceInput);
  }

  /**
   * Mengenwahl im Einmalkauf (Michael 05.10.): Kundinnen kaufen ohne Abo gern
   * mehrere Flaschen. Die Menge wird direkt in data-lines der Einmalkauf-Option
   * geschrieben, damit #buildItems unverändert bleibt; Preis und Zeile über dem
   * Button folgen mit.
   */
  #handleOnceStep = (event) => {
    // Mengenstaffel-Pille: springt direkt auf die Menge der nächsten Stufe
    const tier = event.target.closest?.('[data-once-tier]');
    if (tier && this.contains(tier)) {
      event.preventDefault();
      const target = Number(tier.dataset.tierTarget);
      if (target > 0) this.#setOnceQty(target);
      return;
    }

    const step = event.target.closest?.('[data-once-step]');
    if (!step || !this.contains(step)) return;
    event.preventDefault();
    const input = this.querySelector('[data-once-input]');
    if (!input) return;
    this.#setOnceQty(Number(input.value) + Number(step.dataset.onceStep));
  };

  #handleOnceInput = (event) => {
    if (!event.target?.matches?.('[data-once-input]')) return;
    // Leeres Feld beim Tippen stehen lassen, erst bei gültiger Zahl übernehmen
    if (event.target.value === '') return;
    this.#setOnceQty(Number(event.target.value));
  };

  #setOnceQty(value) {
    const input = this.querySelector('[data-once-input]');
    const radio = this.querySelector('input[name="alp_offer"][value="product_once"]');
    if (!input || !radio) return;

    const max = Number(input.max) || 10;
    const qty = Math.min(max, Math.max(1, Math.round(value) || 1));
    if (String(qty) !== input.value) input.value = String(qty);

    this.querySelector('[data-once-step="-1"]')?.toggleAttribute('disabled', qty <= 1);
    this.querySelector('[data-once-step="1"]')?.toggleAttribute('disabled', qty >= max);

    try {
      const lines = JSON.parse(radio.dataset.lines || '[]');
      if (lines[0]) lines[0].quantity = qty;
      radio.dataset.lines = JSON.stringify(lines);
    } catch {
      return;
    }

    // Mengenstaffel: höchste erreichte Stufe gilt (Shopify wendet von den nicht
    // kombinierbaren Automatik-Rabatten ebenfalls den besten an).
    const tiers = this.#onceTiers(radio);
    const active = tiers.filter((t) => qty >= t.min).pop();
    const next = tiers.find((t) => qty < t.min);

    // Shopify rundet den Prozentrabatt je Stück (5 × 46,67 statt 233,325) — genauso rechnen
    const unitCents = Number(radio.dataset.unitCents);
    const fullCents = unitCents * qty;
    const payCents = active ? Math.round((unitCents * (100 - active.pct)) / 100) * qty : fullCents;
    const total = this.#formatMoney(payCents, radio.dataset.moneyZero);
    if (total) radio.dataset.total = total;
    radio.dataset.summary = `${qty > 1 ? `${qty}× ` : ''}${radio.dataset.summaryTitle || ''}`;

    const qtyEl = this.querySelector('[data-once-qty]');
    if (qtyEl) qtyEl.textContent = String(qty);
    const priceEl = this.querySelector('[data-once-price]');
    if (priceEl && total) priceEl.textContent = total;
    // Grundpreis (PAngV) folgt dem rabattierten Preis
    const unitEl = this.querySelector('[data-once-unit]');
    if (unitEl) {
      const refCents = Number(unitEl.dataset.unitRefCents);
      const unitCents = active ? Math.round((refCents * (100 - active.pct)) / 100) : refCents;
      const unitText = this.#formatMoney(unitCents, radio.dataset.moneyZero);
      if (unitText) unitEl.textContent = unitText;
    }

    const wasEl = this.querySelector('[data-once-was]');
    if (wasEl) {
      wasEl.hidden = !active;
      wasEl.textContent = active ? this.#formatMoney(fullCents, radio.dataset.moneyZero) : '';
    }

    const tierEl = this.querySelector('[data-once-tier]');
    if (tierEl && tiers.length) {
      const fill = (template, values) =>
        Object.entries(values).reduce((text, [key, val]) => text.replaceAll(`{${key}}`, String(val)), template || '');
      let text;
      if (!active) text = fill(radio.dataset.tierNext, { min: next.min, pct: next.pct });
      else if (next) text = fill(radio.dataset.tierActive, { pct: active.pct, min: next.min, next: next.pct });
      else text = fill(radio.dataset.tierMax, { pct: active.pct });
      tierEl.textContent = text;
      tierEl.classList.toggle('is-active', Boolean(active));
      tierEl.dataset.tierTarget = String(next ? next.min : 0);
    }

    if (!radio.checked) radio.checked = true;
    this.#syncSummary();
  }

  /** Stufen aus data-tiers, aufsteigend nach Mindestmenge. */
  #onceTiers(radio) {
    try {
      const tiers = JSON.parse(radio.dataset.tiers || '[]');
      return Array.isArray(tiers)
        ? tiers.filter((t) => t && t.min > 1 && t.pct > 0).sort((a, b) => a.min - b.min)
        : [];
    } catch {
      return [];
    }
  }

  /**
   * Formatiert Cent-Beträge im Shop-Format: Vorlage ist `0 | money` aus Liquid
   * (z. B. „€ 0,00"), deren Nullbetrag durch den echten Betrag ersetzt wird.
   */
  #formatMoney(cents, zeroTemplate) {
    if (!Number.isFinite(cents) || !zeroTemplate) return '';
    const match = zeroTemplate.match(/0([.,])00/);
    if (!match) return '';
    const decimal = match[1];
    const thousands = decimal === ',' ? '.' : ',';
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return zeroTemplate.replace(match[0], `${grouped}${decimal}${fraction}`);
  }

  /**
   * Geschenk in der Staffel → Schritt seiner Lieferung im Abo-Pfad (#alp-pfad-N),
   * sonst der Abo-Pfad als Ganzes. Kein Ziel auf der Seite: nichts tun statt
   * ins Leere springen. Der Link sitzt im <label> der Karte; preventDefault
   * verhindert zugleich, dass der Klick die Karte auswählt.
   */
  #handleGiftLink = (event) => {
    const link = event.target.closest?.('a[data-gift-step]');
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    const target =
      document.getElementById(`alp-pfad-${link.dataset.giftStep}`) || document.getElementById('alp-abo-pfad');
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (target.classList.contains('alp-pfad__step')) {
      target.classList.remove('is-flash');
      void target.offsetWidth;
      target.classList.add('is-flash');
      setTimeout(() => target.classList.remove('is-flash'), 2600);
    }
    history.replaceState(null, '', `#${target.id}`);
  };

  #handleChange = (event) => {
    if (event.target?.name !== 'alp_offer') return;
    this.#syncSummary();
  };

  /**
   * Hält die Zeile über dem Button aktuell. Sobald mehrere Preise gleichzeitig
   * sichtbar sind (die Protokoll-Karte bleibt immer offen), ist ohne diese
   * Zeile nicht eindeutig, was der Button tut.
   */
  #syncSummary() {
    const checked = this.querySelector('input[name="alp_offer"]:checked');
    if (this.summaryEl) this.summaryEl.textContent = checked?.dataset.summary || '';
    if (this.totalEl) this.totalEl.textContent = checked?.dataset.total || '';
    // Button-Text folgt der Auswahl: „Abo hinzufügen" bei Abo-Stufen, sonst der
    // Einmalkauf-Text aus dem Markup (Pascal 21.09.). Ausverkauft-Zustand bleibt unangetastet.
    const ctaText = this.button?.querySelector('.alp-ladder__cta-text');
    if (ctaText && this.button && !this.button.disabled) {
      if (this.defaultCta === undefined) this.defaultCta = ctaText.textContent;
      ctaText.textContent = checked?.dataset.cta || this.defaultCta;
    }
  }

  #handleClick = (event) => {
    event.preventDefault();
    this.#addToCart();
  };

  /** Aktuell gewählte Variante laut URL — null, wenn kein Parameter gesetzt. */
  #currentVariantId() {
    try {
      const value = new URLSearchParams(window.location.search).get('variant');
      const id = Number(value);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  #buildItems() {
    const checked = this.querySelector('input[name="alp_offer"]:checked');
    if (!checked) return [];

    let lines = [];
    try {
      lines = JSON.parse(checked.dataset.lines || '[]');
    } catch {
      return [];
    }
    if (!Array.isArray(lines) || !lines.length) return [];

    // Produktstufen (Kern-Abo, Monats-Anker, Einmalkauf) kaufen die AKTUELL
    // gewählte Variante, nicht die zum Renderzeitpunkt aktive. Nur das
    // Voll-Protokoll kauft fremde Produkte.
    const isProductScope = !String(checked.value || '').startsWith('bundle');
    const liveVariant = isProductScope ? this.#currentVariantId() : null;

    const propertyKey = this.dataset.propertyKey || '_Protokoll';
    const setName = checked.dataset.setName || '';

    // Abo-Gruppe: alle Zeilen eines Abo-Angebots (Abo-Zeilen + Geschenk) tragen dieselbe
    // Kennung _alp_offer. Warenkorb nutzt sie für Darstellung und gemeinsames Entfernen,
    // #addToCart für „ein Abo pro Warenkorb". Kennung = Angebots-ID der Abo-App, sonst Plan-ID.
    const planLine = lines.find((line) => line && line.plan);
    const giftLine = lines.find((line) => line && line.gift);
    const offerKey = planLine ? String(giftLine?.gift || `plan-${planLine.plan}`) : '';

    return lines
      .filter((line) => line && line.id)
      .map((line, index) => {
        const item = {
          id: liveVariant && index === 0 ? liveVariant : Number(line.id),
          quantity: Number(line.quantity) || 1,
        };
        if (line.plan) item.selling_plan = Number(line.plan);
        // Set-Zugehörigkeit nur auf den Protokoll-Zeilen — sie ist das, was
        // Order und Fulfillment brauchen, um die Zeilen zusammenzuziehen.
        if (setName) item.properties = { [propertyKey]: setName };
        // Geschenk der 1. Lieferung (Abo-App): Kennung für die Rabatt-Function
        if (line.gift) item.properties = { ...(item.properties || {}), _alp_gift: String(line.gift) };
        if (offerKey) item.properties = { ...(item.properties || {}), _alp_offer: offerKey };
        return item;
      });
  }

  async #addToCart() {
    const items = this.#buildItems();
    if (!items.length) {
      this.#showError();
      return;
    }

    this.#clearError();
    this.button?.setAttribute('disabled', '');
    this.button?.classList.add('is-loading');

    const deferred = CartLinesUpdateEvent.createPromise?.();
    if (deferred) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: String(item.id),
            quantity: item.quantity,
          })),
          promise: deferred.promise,
        })
      );
    }

    try {
      await this.#removeExistingOffer(items);

      // Section-HTML der Warenkorb-Komponenten gleich mitholen (wie product-form.js), damit
      // der Drawer beim Auto-Öffnen den neuen Inhalt zeigt statt des alten/leeren Stands.
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
        .filter(Boolean);

      const response = await fetch(Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
        }),
      });
      const result = await response.json();

      if (result.status) {
        this.#showError(result.description || result.message);
        deferred?.reject?.(new Error(result.message || 'cart add failed'));
        return;
      }

      const cartResponse = await fetch(`${Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      if (deferred) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            sections: result.sections,
            source: 'alp-offer-ladder',
            itemCount: items.length,
            didError: false,
          },
        });
      } else {
        window.location.href = Theme?.routes?.cart_url || '/cart';
      }
    } catch (error) {
      this.#showError();
      deferred?.reject?.(error);
    } finally {
      this.button?.removeAttribute('disabled');
      this.button?.classList.remove('is-loading');
    }
  }

  /**
   * Ein Abo pro Warenkorb: Liegt dasselbe Abo-Angebot schon im Warenkorb, wird es ersetzt
   * statt aufaddiert. Sonst verschmilzt Shopify die Zeilen (2× → 4× Serum), die Geschenk-Zeile
   * wächst mit, der Geschenk-Rabatt der Abo-App gilt aber nur einmal je Abo-Zeile.
   * Ältere Warenkörbe ohne _alp_offer: Abo-Zeilen gleicher Variante + gleichem Plan.
   */
  async #removeExistingOffer(items) {
    const offerKey = items.find((item) => item.properties?._alp_offer)?.properties._alp_offer;
    if (!offerKey) return;

    const cartResponse = await fetch(`${Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    const planKeys = new Set(items.filter((item) => item.selling_plan).map((item) => `${item.id}:${item.selling_plan}`));

    const updates = {};
    for (const line of cart.items || []) {
      const sameGroup = line.properties?._alp_offer === offerKey;
      const legacyAbo = line.selling_plan_allocation && planKeys.has(`${line.variant_id}:${line.selling_plan_allocation.selling_plan.id}`);
      const legacyGift = line.properties?._alp_gift && line.properties._alp_gift === offerKey;
      if (sameGroup || legacyAbo || legacyGift) updates[line.key] = 0;
    }
    if (!Object.keys(updates).length) return;

    await fetch(Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.dataset.errorFallback || '';
    this.errorEl.hidden = false;
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }
}

if (!customElements.get('alp-offer-ladder')) {
  customElements.define('alp-offer-ladder', OfferLadder);
}
